/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

export enum Currency {
  Aed = "AED",
  Aud = "AUD",
  Bgn = "BGN",
  Brl = "BRL",
  Cad = "CAD",
  Chf = "CHF",
  Cny = "CNY",
  Czk = "CZK",
  Dkk = "DKK",
  Eur = "EUR",
  Gbp = "GBP",
  Hkd = "HKD",
  Hrk = "HRK",
  Huf = "HUF",
  Idr = "IDR",
  Ils = "ILS",
  Inr = "INR",
  Isk = "ISK",
  Jpy = "JPY",
  Krw = "KRW",
  Kwd = "KWD",
  Mad = "MAD",
  Mxn = "MXN",
  Myr = "MYR",
  Nok = "NOK",
  Nzd = "NZD",
  Php = "PHP",
  Pln = "PLN",
  Ron = "RON",
  Rsd = "RSD",
  Rub = "RUB",
  Sar = "SAR",
  Sek = "SEK",
  Sgd = "SGD",
  Thb = "THB",
  Tnd = "TND",
  Try = "TRY",
  Usd = "USD",
  Zar = "ZAR",
}

/* eslint-disable @typescript-eslint/naming-convention */
export type CurrencyType = {
  iso: Currency;
  symbol: string;
  name: string;
  symbol_native: string;
  decimal_digits: number;
  rounding: number;
  code: Currency;
  name_plural: string;
};
/* eslint-enable @typescript-eslint/naming-convention */

export const currencies: CurrencyType[] = [
  {
    iso: Currency.Eur,
    symbol: "€",
    name: "Euro",
    symbol_native: "€",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Eur,
    name_plural: "Euro",
  },
  {
    iso: Currency.Usd,
    symbol: "$",
    name: "US Dollar",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Usd,
    name_plural: "US dollars",
  },
  {
    iso: Currency.Cad,
    symbol: "CA$",
    name: "Canadian Dollar",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Cad,
    name_plural: "Canadian dollars",
  },
  {
    iso: Currency.Aed,
    symbol: "AED",
    name: "United Arab Emirates Dirham",
    symbol_native: "د.إ.‏",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Aed,
    name_plural: "UAE dirhams",
  },
  {
    iso: Currency.Aud,
    symbol: "AU$",
    name: "Australian Dollar",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Aud,
    name_plural: "Australian dollars",
  },
  {
    iso: Currency.Bgn,
    symbol: "BGN",
    name: "Bulgarian Lev",
    symbol_native: "лв.",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Bgn,
    name_plural: "Bulgarian leva",
  },
  {
    iso: Currency.Brl,
    symbol: "R$",
    name: "Brazilian Real",
    symbol_native: "R$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Brl,
    name_plural: "Brazilian reals",
  },
  {
    iso: Currency.Chf,
    symbol: "CHF",
    name: "Swiss Franc",
    symbol_native: "CHF",
    decimal_digits: 2,
    rounding: 0.05,
    code: Currency.Chf,
    name_plural: "Swiss francs",
  },
  {
    iso: Currency.Cny,
    symbol: "CN¥",
    name: "Chinese Yuan",
    symbol_native: "CN¥",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Cny,
    name_plural: "Chinese yuan",
  },
  {
    iso: Currency.Czk,
    symbol: "Kč",
    name: "Czech Republic Koruna",
    symbol_native: "Kč",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Czk,
    name_plural: "Czech Republic korunas",
  },
  {
    iso: Currency.Dkk,
    symbol: "Dkr",
    name: "Danish Krone",
    symbol_native: "kr.",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Dkk,
    name_plural: "Danish kroner",
  },
  {
    iso: Currency.Gbp,
    symbol: "£",
    name: "British Pound Sterling",
    symbol_native: "£",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Gbp,
    name_plural: "British pounds sterling",
  },
  {
    iso: Currency.Hkd,
    symbol: "HK$",
    name: "Hong Kong Dollar",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Hkd,
    name_plural: "Hong Kong dollars",
  },
  {
    iso: Currency.Hrk,
    symbol: "kn",
    name: "Croatian Kuna",
    symbol_native: "kn",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Hrk,
    name_plural: "Croatian kunas",
  },
  {
    iso: Currency.Huf,
    symbol: "Ft",
    name: "Hungarian Forint",
    symbol_native: "Ft",
    decimal_digits: 0,
    rounding: 0,
    code: Currency.Huf,
    name_plural: "Hungarian forints",
  },
  {
    iso: Currency.Idr,
    symbol: "Rp",
    name: "Indonesian Rupiah",
    symbol_native: "Rp",
    decimal_digits: 0,
    rounding: 0,
    code: Currency.Idr,
    name_plural: "Indonesian rupiahs",
  },
  {
    iso: Currency.Ils,
    symbol: "₪",
    name: "Israeli New Sheqel",
    symbol_native: "₪",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Ils,
    name_plural: "Israeli new sheqels",
  },
  {
    iso: Currency.Inr,
    symbol: "Rs",
    name: "Indian Rupee",
    symbol_native: "টকা",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Inr,
    name_plural: "Indian rupees",
  },
  {
    iso: Currency.Isk,
    symbol: "Ikr",
    name: "Icelandic Króna",
    symbol_native: "kr",
    decimal_digits: 0,
    rounding: 0,
    code: Currency.Isk,
    name_plural: "Icelandic krónur",
  },
  {
    iso: Currency.Jpy,
    symbol: "¥",
    name: "Japanese Yen",
    symbol_native: "￥",
    decimal_digits: 0,
    rounding: 0,
    code: Currency.Jpy,
    name_plural: "Japanese yen",
  },
  {
    iso: Currency.Krw,
    symbol: "₩",
    name: "South Korean Won",
    symbol_native: "₩",
    decimal_digits: 0,
    rounding: 0,
    code: Currency.Krw,
    name_plural: "South Korean won",
  },
  {
    iso: Currency.Kwd,
    symbol: "KD",
    name: "Kuwaiti Dinar",
    symbol_native: "د.ك.‏",
    decimal_digits: 3,
    rounding: 0,
    code: Currency.Kwd,
    name_plural: "Kuwaiti dinars",
  },
  {
    iso: Currency.Mad,
    symbol: "MAD",
    name: "Moroccan Dirham",
    symbol_native: "د.م.‏",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Mad,
    name_plural: "Moroccan dirhams",
  },
  {
    iso: Currency.Mxn,
    symbol: "MX$",
    name: "Mexican Peso",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Mxn,
    name_plural: "Mexican pesos",
  },
  {
    iso: Currency.Myr,
    symbol: "RM",
    name: "Malaysian Ringgit",
    symbol_native: "RM",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Myr,
    name_plural: "Malaysian ringgits",
  },
  {
    iso: Currency.Nok,
    symbol: "Nkr",
    name: "Norwegian Krone",
    symbol_native: "kr",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Nok,
    name_plural: "Norwegian kroner",
  },
  {
    iso: Currency.Nzd,
    symbol: "NZ$",
    name: "New Zealand Dollar",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Nzd,
    name_plural: "New Zealand dollars",
  },
  {
    iso: Currency.Php,
    symbol: "₱",
    name: "Philippine Peso",
    symbol_native: "₱",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Php,
    name_plural: "Philippine pesos",
  },
  {
    iso: Currency.Pln,
    symbol: "zł",
    name: "Polish Zloty",
    symbol_native: "zł",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Pln,
    name_plural: "Polish zlotys",
  },
  {
    iso: Currency.Ron,
    symbol: "RON",
    name: "Romanian Leu",
    symbol_native: "RON",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Ron,
    name_plural: "Romanian lei",
  },
  {
    iso: Currency.Rsd,
    symbol: "din.",
    name: "Serbian Dinar",
    symbol_native: "дин.",
    decimal_digits: 0,
    rounding: 0,
    code: Currency.Rsd,
    name_plural: "Serbian dinars",
  },
  {
    iso: Currency.Rub,
    symbol: "RUB",
    name: "Russian Ruble",
    symbol_native: "₽.",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Rub,
    name_plural: "Russian rubles",
  },
  {
    iso: Currency.Sar,
    symbol: "SR",
    name: "Saudi Riyal",
    symbol_native: "ر.س.‏",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Sar,
    name_plural: "Saudi riyals",
  },
  {
    iso: Currency.Sek,
    symbol: "Skr",
    name: "Swedish Krona",
    symbol_native: "kr",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Sek,
    name_plural: "Swedish kronor",
  },
  {
    iso: Currency.Sgd,
    symbol: "S$",
    name: "Singapore Dollar",
    symbol_native: "$",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Sgd,
    name_plural: "Singapore dollars",
  },
  {
    iso: Currency.Thb,
    symbol: "฿",
    name: "Thai Baht",
    symbol_native: "฿",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Thb,
    name_plural: "Thai baht",
  },
  {
    iso: Currency.Tnd,
    symbol: "DT",
    name: "Tunisian Dinar",
    symbol_native: "د.ت.‏",
    decimal_digits: 3,
    rounding: 0,
    code: Currency.Tnd,
    name_plural: "Tunisian dinars",
  },
  {
    iso: Currency.Try,
    symbol: "TL",
    name: "Turkish Lira",
    symbol_native: "TL",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Try,
    name_plural: "Turkish Lira",
  },
  {
    iso: Currency.Zar,
    symbol: "R",
    name: "South African Rand",
    symbol_native: "R",
    decimal_digits: 2,
    rounding: 0,
    code: Currency.Zar,
    name_plural: "South African rand",
  },
];
